<template>
    <LoadingStencil v-if="isLoading" :uiHeight="'40rem'" />
    <ag-grid-vue v-else
        class="ath-ag-grid-table ag-theme-bbi dw-10 dh-10"
        :class="[
            fitContentHeight && hasData ? 'ag-root-wrapper-body-fit-content-height' : '',
            !hasData ? 'ath-ag-grid-no-data' : ''
        ]"
        :autoSizeStrategy="engine.autoSizeStrategy"
        :columnDefs="engine.columnDefs"
        :columnTypes="engine.columnTypes"
        :pagination="engine.pagination"
        :treeData="engine.treeData"
        :groupDefaultExpanded="engine.groupDefaultExpanded"
        :autoGroupColumnDef="engine.autoGroupColumnDef"
        :getDataPath="engine.getDataPath"
        :rowData="rows"
        :pinnedTopRowData="pinnedRows"
        :getRowId="engine.getRowId"
        :rowModelType="engine.rowModelType"
        :isServerSideGroup="engine.isServerSideGroup"
        :getServerSideGroupKey="engine.getServerSideGroupKey"
        :serverSideDatasource="engine.serverSideDatasource"
        :defaultColDef="engine.defaultColDef"
        :getContextMenuItems="engine.getContextMenuItems"
        :enableRangeSelection="engine.enableRangeSelection"
        :sideBar="engine.sideBar"
        :rowSelection="engine.rowSelection"
        :checkboxSelection="engine.checkboxSelection"
        :rowMultiSelectWithClick="engine.rowMultiSelectWithClick"
        @rowClicked="engine.rowClicked"
        v-bind="customProps"
        @grid-ready="onGridReady"
        @firstDataRendered="onFirstDataRendered" />
</template>
<script>
import { isNeArray } from '@aretheyhappy/utils';
import { AgGridVue } from '../../../vendor/ag-grid/AgGridVue.js';
import LoadingStencil from '../../atoms/stencils/LoadingStencil.vue';

// If the columns collectively take up less than this fraction of the grid width,
// switch the size strategy so they stretch to fill the container.
const FIT_GRID_WIDTH_THRESHOLD = 0.95;

export default {
    name: 'mead-ag-grid-table',
    props: {
        /**
         * The ag-grid engine/configuration object (from AgGridHelper /
         * TableEngineConverter) that supplies all the grid's column defs, row
         * model settings, selection behavior, and event handlers.
         */
        engine: {type: Object, default: () => {}},
        /**
         * The row data to render in the grid body.
         */
        rows: {type: Array, default: () => undefined},
        /**
         * Row data that stays pinned to the top of the grid regardless of scrolling.
         */
        pinnedRows: {type: Array, default: () => []},
        /**
         * Additional props/attributes bound directly onto the underlying
         * ag-grid-vue component, for options not otherwise exposed.
         */
        customProps: {type: Object, default: () => {}},
        /**
         * Whether the grid is in a loading state. While true, a LoadingStencil
         * placeholder is shown instead of the grid.
         */
        isLoading: {type: Boolean, default: false},
        /**
         * Whether the grid's wrapper should shrink to fit its content height
         * rather than filling the available space, applied only when there is data.
         */
        fitContentHeight: {type: Boolean, default: () => false},
    },
    components: {
        AgGridVue,
        LoadingStencil,
    },
    data: function () {
        return {
            gridApi: undefined,
        };
    },
    methods: {
        onGridReady:function (params) {
            this.gridApi = params.api;
            this.$emit('onGridApiReady', this.gridApi)
        },
        onFirstDataRendered: function (params) {
            this.stretchColumnsToFitIfNarrow(params);
            this.$emit('onFirstDataRendered', params)
        },
        /**
         * When the total width of the displayed columns is significantly smaller
         * than the grid container, switch the size strategy to FIT_GRID_WIDTH so
         * the columns stretch to fill the container instead of leaving whitespace.
         */
        stretchColumnsToFitIfNarrow: function (params) {
            setTimeout(() => {
                const api = params?.api;
                const columnApi = params?.columnApi;
                const columns = api?.getAllDisplayedColumns
                    ? api.getAllDisplayedColumns()
                    : columnApi?.getAllDisplayedColumns();

                if (!isNeArray(columns) || !this.$el) return;

                const totalColumnsWidth = columns.reduce((sum, col) => sum + col.getActualWidth(), 0);
                const gridContainerWidth = this.$el.clientWidth;

                if (totalColumnsWidth >= gridContainerWidth * FIT_GRID_WIDTH_THRESHOLD) return;

                this.engine?.setSizeStrategy?.({
                    type: MeadCommon.AUTO_SIZE_TYPE.FIT_GRID_WIDTH,
                });
                if (typeof api?.sizeColumnsToFit === 'function') {
                    api.sizeColumnsToFit();
                }
            }, 60);
        },
    },
    computed: {
        hasData: function () {
            return isNeArray(this.rows) || isNeArray(this.pinnedRows) || this.engine.rowModelType === 'serverSide';
        },
    },
    created: function () {
        MeadCommon.AgGridHelper.registerModules();
    }
}
</script>
<style lang="scss" scoped>
.ath-ag-grid-no-data {
    height: 40rem;
}
</style>